import BusinessImpactIcon from "ac-components/ac-icons/BusinessImpactIcon";
import ComplianceIcon from "ac-components/ac-icons/ComplianceIcon";
import DataSensitivityIcon from "ac-components/ac-icons/DataSensitivityIcon";
import ExtFacingIcon from "ac-components/ac-icons/ExtFacingIcon";

// Priority order for known tag types (for consistent display ordering)
// Tags are displayed in this order: business-impact -> compliance -> data-sensitivity -> ext-facing -> Other
export const TAG_PRIORITY_ORDER: string[] = [
  "business-impact",
  "compliance",
  "data-sensitivity",
  "ext-facing",
];

// Color definitions
export const TAG_COLORS = {
  RED: { background: "#FDECEB", foreground: "#E3352D" },
  ORANGE: { background: "#FCEBDC", foreground: "#E3642D" },
  YELLOW: { background: "#F6F5BF", foreground: "#DFA700" },
  GREEN: { background: "#D7F7E3", foreground: "#009537" },
  NEUTRAL: {
    background: "var(--background-tertiary)",
    foreground: "var(--foreground-default)",
  },
} as const;

type TagColor = (typeof TAG_COLORS)[keyof typeof TAG_COLORS];

// Color lookup maps (O(1) lookup instead of if-else chains)
const SEVERITY_COLORS: Record<string, TagColor> = {
  critical: TAG_COLORS.RED,
  high: TAG_COLORS.ORANGE,
  medium: TAG_COLORS.YELLOW,
  low: TAG_COLORS.GREEN,
};

const BOOLEAN_COLORS: Record<string, TagColor> = {
  true: TAG_COLORS.RED,
  false: TAG_COLORS.GREEN,
};

const DATA_SENSITIVITY_COLORS: Record<string, TagColor> = {
  high: TAG_COLORS.RED,
  medium: TAG_COLORS.ORANGE,
  low: TAG_COLORS.YELLOW,
};

// Reusable color getters
const getSeverityColor = (value: string): TagColor =>
  SEVERITY_COLORS[value.toLowerCase()] ?? TAG_COLORS.NEUTRAL;

const getBooleanColor = (value: string): TagColor =>
  BOOLEAN_COLORS[value.toLowerCase()] ?? TAG_COLORS.NEUTRAL;

const getDataSensitivityColor = (value: string): TagColor =>
  DATA_SENSITIVITY_COLORS[value.toLowerCase()] ?? TAG_COLORS.NEUTRAL;

// Tag type config
export interface TagTypeConfig {
  icon: React.ComponentType<any>;
  getColor: (value: string) => TagColor;
}

export const KNOWN_TAG_TYPES: Record<string, TagTypeConfig> = {
  "business-impact": { icon: BusinessImpactIcon, getColor: getSeverityColor },
  "ext-facing": { icon: ExtFacingIcon, getColor: getBooleanColor },
  "data-sensitivity": {
    icon: DataSensitivityIcon,
    getColor: getDataSensitivityColor,
  },
  compliance: { icon: ComplianceIcon, getColor: getBooleanColor },
};

// Parsed tag interface
export interface ParsedTag {
  key: string;
  value: string;
  displayKey: string;
  displayValue: string;
  isKnown: boolean;
  config?: TagTypeConfig;
  color: TagColor;
}
